/**
 * BillsOnboarding - Hybrid Plaid + Gamified Manual Entry
 * Features:
 * - Choice Screen (Plaid vs Manual)
 * - Plaid Link integration for auto-sync
 * - Gamified manual entry with "Rule of 3" and Paper Chaser rank
 * - Plaid escape hatch in manual mode
 * - Intent-based paywall for premium features
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePlaidLink } from 'react-plaid-link';
import { apiGet, apiPost, getToken } from '@/utils/api';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { usePremiumAction, PREMIUM_ACTIONS } from '@/contexts/PremiumActionContext';
import { useSubscription } from '@/contexts/SubscriptionContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Guapo Character Image URL
const GUAPO_IMAGE_URL = 'https://customer-assets.emergentagent.com/job_budget-buddy-4768/artifacts/qhory5fo_Guap.png';

// Haptic feedback helper
const triggerHaptic = (type = 'medium') => {
  if (window.Capacitor?.Plugins?.Haptics) {
    const intensity = { light: 'Light', medium: 'Medium', heavy: 'Heavy' }[type] || 'Medium';
    window.Capacitor.Plugins.Haptics.impact({ style: intensity });
  } else if (navigator.vibrate) {
    const durations = { light: 10, medium: 25, heavy: 50 };
    navigator.vibrate(durations[type] || 25);
  }
};

// Confetti celebration
const triggerConfetti = () => {
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#10B981', '#F59E0B', '#8B5CF6', '#EC4899']
  });
};

// Bill categories
const BILL_CATEGORIES = [
  { value: '🏠 Housing', label: '🏠 Housing' },
  { value: '💡 Utilities', label: '💡 Utilities' },
  { value: '🚗 Transportation', label: '🚗 Transportation' },
  { value: '📱 Subscriptions', label: '📱 Subscriptions' },
  { value: '🛡️ Insurance', label: '🛡️ Insurance' },
  { value: '🍔 Food & Dining', label: '🍔 Food & Dining' },
  { value: '🏥 Healthcare', label: '🏥 Healthcare' },
  { value: '📋 Other', label: '📋 Other' }
];

// ==================== CHOICE SCREEN ====================
function ChoiceScreen({ onSelectPlaid, onSelectManual, plaidReady }) {
  const { queuePremiumAction } = usePremiumAction();
  const { canAccessFeature } = useSubscription();
  const [isLoading, setIsLoading] = useState(false);
  
  // Handle Subscription Hunt button click
  const handleStartSubscriptionHunt = async () => {
    setIsLoading(true);
    try {
      // Queue the action - this will check subscription and show paywall if needed
      queuePremiumAction(
        PREMIUM_ACTIONS.SUBSCRIPTION_HUNT,
        () => {
          // This callback runs when user has premium access
          onSelectPlaid();
        },
        { source: 'choice_screen' }
      );
    } catch (error) {
      console.error('Error starting subscription hunt:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      // Reset loading state after a short delay to allow for UI transitions
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  // Check if user already has premium
  const hasPremium = canAccessFeature('bank_sync');
  
  // Button is always active unless currently loading
  const isButtonActive = !isLoading;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-lg mx-auto p-6"
    >
      <div className="text-center mb-8">
        <div className="text-5xl mb-4">🔍</div>
        <h1 className="text-2xl font-black text-gray-900 mb-2">
          Subscription Hunter
        </h1>
        <p className="text-gray-500">
          Find and track your recurring subscriptions
        </p>
      </div>

      <div className="space-y-4">
        {/* Primary: Plaid Auto-Sync */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleStartSubscriptionHunt}
          disabled={isLoading}
          className={`w-full p-6 rounded-2xl border-2 text-left transition-all relative ${
            isButtonActive
              ? hasPremium
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 border-emerald-400 text-white shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50'
                : 'bg-white border-emerald-300 hover:border-emerald-400 hover:shadow-lg hover:shadow-emerald-500/20'
              : 'bg-gray-50 border-gray-200 cursor-wait'
          }`}
          data-testid="plaid-option"
        >
          {/* Recommended Badge - Top Right */}
          <span className="absolute -top-2 right-4 text-xs px-3 py-1 bg-emerald-500 text-white font-semibold rounded-full shadow-sm">
            Recommended
          </span>
          
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              hasPremium ? 'bg-white/20' : 'bg-gradient-to-br from-emerald-100 to-teal-100'
            }`}>
              <span className="text-3xl">🎯</span>
            </div>
            <div className="flex-1">
              <div className={`font-bold text-lg flex items-center gap-2 ${hasPremium ? 'text-white' : 'text-gray-900'}`}>
                Auto-Sync Subscriptions
                <span className="text-base" title="Premium Feature">💎</span>
              </div>
              <div className={`text-sm ${hasPremium ? 'opacity-80' : 'text-gray-500'}`}>
                Link your bank to find hidden subscriptions automatically
              </div>
            </div>
            {isLoading ? (
              <div className="px-4 py-2 bg-gray-200 text-gray-500 text-sm font-medium rounded-lg flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                Loading...
              </div>
            ) : hasPremium ? (
              <div className="text-2xl text-white">→</div>
            ) : (
              <div className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg transition-all">
                Start the Hunt 🎯
              </div>
            )}
          </div>
          
          {/* Premium badge for non-premium users */}
          {!hasPremium && !isLoading && (
            <div className="mt-3 flex items-center gap-2 text-xs text-emerald-600">
              <span className="px-2 py-0.5 bg-emerald-100 rounded-full font-medium">Premium Feature</span>
              <span className="text-gray-500">• 7-day free trial</span>
            </div>
          )}
        </motion.button>

        {/* Centered OR Divider */}
        <div className="flex items-center gap-4 py-4">
          <div className="flex-1 h-px bg-gray-200"></div>
          <span className="text-gray-400 text-sm font-semibold px-2">OR</span>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>

        {/* Secondary: Manual Entry */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onSelectManual}
          className="w-full p-6 rounded-2xl border-2 border-gray-200 bg-white text-left hover:border-gray-300 hover:shadow-md transition-all"
          data-testid="manual-option"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center">
              <span className="text-3xl">✏️</span>
            </div>
            <div className="flex-1">
              <div className="font-bold text-lg text-gray-900">
                Enter Manually
              </div>
              <div className="text-sm text-gray-500">
                Add your subscriptions one by one
              </div>
            </div>
            <div className="text-2xl text-gray-400">→</div>
          </div>
        </motion.button>
      </div>

      {/* Security Note */}
      <div className="mt-8 mb-24 p-4 bg-gray-50 rounded-xl">
        <div className="flex items-start gap-3">
          <span className="text-xl">🔐</span>
          <div className="text-xs text-gray-500">
            <strong className="text-gray-700">Your security matters.</strong> We use Plaid, the same service trusted by Venmo, Robinhood, and thousands of financial apps. We never store your bank login credentials.
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ==================== ALIAS CATCHER UTILITIES ====================

/**
 * Regex patterns to detect Apple and Google ecosystem charges
 * These services mask the true merchant name (Netflix, Spotify, etc.)
 */
const ECOSYSTEM_PATTERNS = {
  apple: /apple\.com|apple\s*pay|itunes|app\s*store|apple\s*services|apple\s*bill/i,
  google: /google\s*\*|google\s*play|google\s*services|google\s*storage|goog\s*\*/i
};

/**
 * Checks if a bill is from Apple or Google ecosystem
 * @param {object} bill - Bill object with name and merchant_name fields
 * @returns {{ isEcosystem: boolean, platform: 'apple' | 'google' | null }}
 */
const detectEcosystemBill = (bill) => {
  const nameToCheck = `${bill.name || ''} ${bill.merchant_name || ''}`.toLowerCase();
  
  if (ECOSYSTEM_PATTERNS.apple.test(nameToCheck)) {
    return { isEcosystem: true, platform: 'apple' };
  }
  if (ECOSYSTEM_PATTERNS.google.test(nameToCheck)) {
    return { isEcosystem: true, platform: 'google' };
  }
  return { isEcosystem: false, platform: null };
};

/**
 * Filter bills to separate ecosystem charges from regular bills
 * @param {array} bills - Array of bills from Plaid
 * @returns {{ regularBills: array, ecosystemBills: array }}
 */
const categorizeEcosystemBills = (bills) => {
  const regularBills = [];
  const ecosystemBills = [];
  
  bills.forEach(bill => {
    const { isEcosystem, platform } = detectEcosystemBill(bill);
    if (isEcosystem) {
      ecosystemBills.push({ ...bill, platform, original_plaid_name: bill.name });
    } else {
      regularBills.push(bill);
    }
  });
  
  return { regularBills, ecosystemBills };
};

// ==================== GUAPO ALIAS CATCHER MODAL ====================
function GuapoAliasCatcher({ ecosystemBills, onComplete, onSkip }) {
  const [aliases, setAliases] = useState(
    ecosystemBills.reduce((acc, bill, index) => {
      acc[index] = '';
      return acc;
    }, {})
  );

  const updateAlias = (index, value) => {
    setAliases(prev => ({ ...prev, [index]: value }));
  };

  const handleComplete = () => {
    // Map bills with their new aliases
    const aliasedBills = ecosystemBills.map((bill, index) => ({
      ...bill,
      name: aliases[index] || bill.name, // Use alias if provided, else original
      alias: aliases[index] || null,
      original_plaid_name: bill.original_plaid_name || bill.name,
      is_ecosystem_charge: true
    }));
    onComplete(aliasedBills);
  };

  const allAliasesProvided = Object.values(aliases).every(a => a.trim() !== '');
  const someAliasesProvided = Object.values(aliases).some(a => a.trim() !== '');

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="max-w-lg mx-auto p-6"
    >
      {/* Guapo Header Card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-1 mb-6">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-400/50 via-orange-400/50 to-red-400/50 animate-pulse" />
        
        <div className="relative bg-slate-900 rounded-xl p-5">
          <div className="flex items-start gap-4">
            {/* Guapo Avatar */}
            <div className="relative flex-shrink-0">
              <div className="w-16 h-16 rounded-full overflow-hidden shadow-lg shadow-orange-500/30">
                <img 
                  src={GUAPO_IMAGE_URL}
                  alt="Guapo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-500 rounded-full border-2 border-slate-900 flex items-center justify-center">
                <span className="text-[10px] text-white font-bold">!</span>
              </div>
            </div>
            
            {/* Message */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-amber-400 font-bold">GUAPO</span>
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                  Alias Catcher
                </span>
              </div>
              <p className="text-white text-sm leading-relaxed">
                <span className="font-bold">Hold up!</span> 🛑 I see some charges from{' '}
                <span className="text-amber-400 font-bold">Apple</span> or{' '}
                <span className="text-green-400 font-bold">Google</span>. 
                They hide the real names of your subscriptions. 
                <span className="text-amber-300"> What are these actually for?</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Ecosystem Bills List */}
      <div className="space-y-4 mb-6">
        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
          🔍 Mystery Charges Found ({ecosystemBills.length})
        </h3>
        
        {ecosystemBills.map((bill, index) => (
          <motion.div
            key={bill.id || index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white border-2 border-gray-200 rounded-2xl p-4 hover:border-gray-300 transition-colors"
          >
            {/* Platform Badge */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  bill.platform === 'apple' 
                    ? 'bg-gray-900 text-white' 
                    : 'bg-gradient-to-r from-blue-500 to-green-500 text-white'
                }`}>
                  {bill.platform === 'apple' ? '🍎 Apple' : '🔷 Google'}
                </span>
                <span className="text-xs text-gray-500">
                  Detected charge
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900">
                ${bill.amount.toFixed(2)}
              </span>
            </div>

            {/* Original Name */}
            <div className="mb-3 p-2 bg-gray-100 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Shows up as:</p>
              <p className="text-sm font-medium text-gray-700 font-mono">
                "{bill.original_plaid_name || bill.name}"
              </p>
              {bill.last_date && (
                <p className="text-xs text-gray-500 mt-1">
                  Last charged: {new Date(bill.last_date).toLocaleDateString()}
                </p>
              )}
            </div>

            {/* Alias Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What is this actually for?
              </label>
              <input
                type="text"
                placeholder="e.g., Netflix, Spotify, iCloud Storage, YouTube Premium..."
                value={aliases[index]}
                onChange={(e) => updateAlias(index, e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all text-base"
                data-testid={`alias-input-${index}`}
              />
              
              {/* Quick Suggestions */}
              <div className="flex flex-wrap gap-2 mt-2">
                {bill.platform === 'apple' ? (
                  <>
                    {['Netflix', 'Spotify', 'iCloud', 'Apple Music', 'Apple TV+', 'Disney+'].map(suggestion => (
                      <button
                        key={suggestion}
                        onClick={() => updateAlias(index, suggestion)}
                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-amber-100 text-gray-600 hover:text-amber-700 rounded-full transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </>
                ) : (
                  <>
                    {['YouTube Premium', 'Google One', 'Spotify', 'Netflix', 'HBO Max', 'Hulu'].map(suggestion => (
                      <button
                        key={suggestion}
                        onClick={() => updateAlias(index, suggestion)}
                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-blue-100 text-gray-600 hover:text-blue-700 rounded-full transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={handleComplete}
          disabled={!someAliasesProvided && ecosystemBills.length > 0}
          className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-amber-500/30"
          data-testid="complete-aliases-btn"
        >
          {allAliasesProvided 
            ? '✓ Save Names & Continue' 
            : someAliasesProvided 
              ? 'Save What I Named & Continue'
              : 'Name at least one to continue'}
        </button>
        
        <button
          onClick={onSkip}
          className="w-full py-3 text-gray-500 hover:text-gray-700 font-medium transition-colors"
        >
          Skip — I'll rename these later
        </button>
      </div>

      {/* Info Note */}
      <div className="mt-6 p-3 bg-blue-50 rounded-xl">
        <p className="text-xs text-blue-700">
          <span className="font-bold">💡 Pro tip:</span> Naming these helps you track your real subscriptions. 
          We'll keep the original Plaid data for automatic matching in future syncs.
        </p>
      </div>
    </motion.div>
  );
}

// ==================== PLAID BILLS REVIEW (Enhanced with Alias Catcher) ====================
function PlaidBillsReview({ bills, onConfirm, onBack, loading }) {
  // Separate ecosystem charges from regular bills
  const { regularBills: initialRegularBills, ecosystemBills } = categorizeEcosystemBills(bills);
  
  const [showAliasCatcher, setShowAliasCatcher] = useState(ecosystemBills.length > 0);
  const [processedEcosystemBills, setProcessedEcosystemBills] = useState([]);
  const [regularBills, setRegularBills] = useState(initialRegularBills);
  
  // Combine all bills for display after alias catcher is done
  const allBills = [...regularBills, ...processedEcosystemBills];
  
  const [selectedBills, setSelectedBills] = useState(
    allBills.map(b => ({ ...b, selected: true }))
  );

  // Update selected bills when processed ecosystem bills change
  useEffect(() => {
    const combined = [...regularBills, ...processedEcosystemBills];
    setSelectedBills(combined.map(b => ({ ...b, selected: true })));
  }, [regularBills, processedEcosystemBills]);

  const handleAliasComplete = (aliasedBills) => {
    setProcessedEcosystemBills(aliasedBills);
    setShowAliasCatcher(false);
    triggerHaptic('medium');
  };

  const handleAliasSkip = () => {
    // Keep original names but mark as ecosystem charges
    const skippedBills = ecosystemBills.map(bill => ({
      ...bill,
      original_plaid_name: bill.name,
      is_ecosystem_charge: true
    }));
    setProcessedEcosystemBills(skippedBills);
    setShowAliasCatcher(false);
  };

  const toggleBill = (index) => {
    setSelectedBills(prev => 
      prev.map((b, i) => i === index ? { ...b, selected: !b.selected } : b)
    );
  };

  const selectedCount = selectedBills.filter(b => b.selected).length;
  const totalAmount = selectedBills
    .filter(b => b.selected)
    .reduce((sum, b) => sum + b.amount, 0);

  // Show Alias Catcher first if there are ecosystem charges
  if (showAliasCatcher && ecosystemBills.length > 0) {
    return (
      <GuapoAliasCatcher
        ecosystemBills={ecosystemBills}
        onComplete={handleAliasComplete}
        onSkip={handleAliasSkip}
      />
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="max-w-lg mx-auto p-6"
    >
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6"
      >
        ← Back
      </button>

      <div className="text-center mb-6">
        <div className="text-4xl mb-2">🎉</div>
        <h2 className="text-xl font-bold text-gray-900">
          Found {allBills.length} Recurring Bills!
        </h2>
        <p className="text-gray-500 text-sm">
          Select the bills you want to track in GuapGoals
        </p>
        {processedEcosystemBills.length > 0 && (
          <p className="text-amber-600 text-xs mt-1">
            ✓ {processedEcosystemBills.length} Apple/Google charges renamed
          </p>
        )}
      </div>

      {/* Bills List */}
      <div className="space-y-2 mb-6 max-h-96 overflow-y-auto">
        {selectedBills.map((bill, index) => (
          <motion.div
            key={bill.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            onClick={() => toggleBill(index)}
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
              bill.selected 
                ? 'border-emerald-500 bg-emerald-50' 
                : 'border-gray-200 bg-white opacity-60'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                bill.selected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
              }`}>
                {bill.selected && <span className="text-white text-sm">✓</span>}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{bill.name}</span>
                  {bill.is_ecosystem_charge && (
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                      bill.platform === 'apple' 
                        ? 'bg-gray-900 text-white' 
                        : 'bg-blue-500 text-white'
                    }`}>
                      {bill.platform === 'apple' ? '🍎' : '🔷'}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {bill.category} • {bill.frequency}
                  {bill.original_plaid_name && bill.original_plaid_name !== bill.name && (
                    <span className="text-gray-400 ml-1">
                      (via {bill.platform === 'apple' ? 'Apple' : 'Google'})
                    </span>
                  )}
                </div>
              </div>
              <div className="font-bold text-gray-900">
                ${bill.amount.toFixed(2)}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Summary */}
      <div className="p-4 bg-gray-100 rounded-xl mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">Selected Bills</span>
          <span className="font-bold">{selectedCount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Monthly Total</span>
          <span className="font-bold text-lg text-emerald-600">
            ${totalAmount.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Confirm Button */}
      <button
        onClick={() => onConfirm(selectedBills.filter(b => b.selected))}
        disabled={loading || selectedCount === 0}
        className="w-full py-4 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600 disabled:opacity-50 transition-colors"
      >
        {loading ? 'Adding Bills...' : `Add ${selectedCount} Bills to Budget`}
      </button>
    </motion.div>
  );
}

// ==================== GAMIFIED MANUAL ENTRY ====================
function GamifiedManualEntry({ 
  bills, 
  onAddBill, 
  onRemoveBill,
  onSwitchToPlaid, 
  onUnlockDashboard,
  onSaveAndExplore,
  loading,
  plaidReady
}) {
  const [newBill, setNewBill] = useState({
    name: '',
    amount: '',
    category: '',
    due_day: '',
    frequency: 'Monthly'
  });
  const [showPaperChaserUnlock, setShowPaperChaserUnlock] = useState(false);
  // Validation errors state for visual feedback
  const [fieldErrors, setFieldErrors] = useState({
    name: false,
    amount: false,
    category: false,
    due_day: false
  });

  const billCount = bills.length;
  const canUnlock = billCount >= 3;
  const isPaperChaser = billCount >= 5;

  // Trigger Paper Chaser celebration
  useEffect(() => {
    if (billCount === 5 && !showPaperChaserUnlock) {
      setShowPaperChaserUnlock(true);
      triggerConfetti();
      triggerHaptic('heavy');
      toast.success('🎉 PAPER CHASER rank unlocked!', {
        duration: 5000
      });
    }
  }, [billCount, showPaperChaserUnlock]);

  const handleAddBill = () => {
    // Validate all required fields including due_day
    const errors = {
      name: !newBill.name.trim(),
      amount: !newBill.amount || parseFloat(newBill.amount) <= 0,
      category: !newBill.category,
      due_day: !newBill.due_day || parseInt(newBill.due_day) < 1 || parseInt(newBill.due_day) > 31
    };
    
    setFieldErrors(errors);
    
    // Check if any errors exist
    const hasErrors = Object.values(errors).some(err => err);
    if (hasErrors) {
      // Build specific error message
      const missingFields = [];
      if (errors.name) missingFields.push('bill name');
      if (errors.amount) missingFields.push('amount');
      if (errors.category) missingFields.push('category');
      if (errors.due_day) missingFields.push('due date (1-31)');
      
      toast.error(`Please fill in: ${missingFields.join(', ')}`);
      triggerHaptic('heavy'); // Error feedback
      return;
    }

    onAddBill({
      ...newBill,
      amount: parseFloat(newBill.amount),
      due_day: parseInt(newBill.due_day)
    });

    // Reset form and errors
    setNewBill({
      name: '',
      amount: '',
      category: '',
      due_day: '',
      frequency: 'Monthly'
    });
    setFieldErrors({
      name: false,
      amount: false,
      category: false,
      due_day: false
    });

    triggerHaptic('light');
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="max-w-lg mx-auto p-6"
    >
      {/* Plaid Escape Hatch - Always at top */}
      {plaidReady && (
        <motion.button
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={onSwitchToPlaid}
          className="w-full mb-6 p-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:shadow-lg transition-all"
          data-testid="plaid-escape-hatch"
        >
          <span className="text-xl">🔒</span>
          <span>Tired of typing? Tap here to securely link your bank instead</span>
        </motion.button>
      )}

      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-1">
          Add Your Bills Manually
        </h2>
        <p className="text-gray-500 text-sm">
          Let's start with the heavy hitters!
        </p>
      </div>

      {/* Progress Bar to Paper Chaser */}
      <div className="mb-6">
        <div className="flex justify-between text-xs mb-2">
          <span className="text-gray-500">Progress to PAPER CHASER 📄🏃‍♂️</span>
          <span className="font-bold text-amber-600">{Math.min(billCount, 5)}/5</span>
        </div>
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-amber-400 to-amber-600"
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(billCount / 5 * 100, 100)}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        {isPaperChaser ? (
          <div className="mt-2 text-center">
            <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
              🎉 PAPER CHASER UNLOCKED!
            </span>
          </div>
        ) : (
          <p className="text-xs text-gray-500 mt-2 text-center">
            Log {5 - billCount} more bill{5 - billCount !== 1 ? 's' : ''} to instantly unlock the PAPER CHASER rank! 📄🏃‍♂️
          </p>
        )}
      </div>

      {/* Rule of 3 Message */}
      {billCount < 3 && (
        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🎯</span>
            <div>
              <p className="text-emerald-800 font-medium text-sm">
                Add your 3 biggest bills to unlock your dashboard!
              </p>
              <p className="text-emerald-600 text-xs mt-1">
                {3 - billCount} more to go...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bill Entry Form */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="col-span-2">
            <input
              type="text"
              placeholder="Bill name *"
              value={newBill.name}
              onChange={(e) => {
                setNewBill({ ...newBill, name: e.target.value });
                if (fieldErrors.name && e.target.value.trim()) {
                  setFieldErrors({ ...fieldErrors, name: false });
                }
              }}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors ${
                fieldErrors.name ? 'border-red-500 bg-red-50' : 'border-gray-200'
              }`}
              data-testid="bill-name-input"
              aria-label="Bill name"
              aria-invalid={fieldErrors.name}
              aria-describedby={fieldErrors.name ? "name-error" : undefined}
            />
            {fieldErrors.name && (
              <p id="name-error" className="text-red-500 text-xs mt-1">Bill name is required</p>
            )}
          </div>
          <div>
            <input
              type="number"
              placeholder="Amount *"
              value={newBill.amount}
              onChange={(e) => {
                setNewBill({ ...newBill, amount: e.target.value });
                if (fieldErrors.amount && e.target.value && parseFloat(e.target.value) > 0) {
                  setFieldErrors({ ...fieldErrors, amount: false });
                }
              }}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors ${
                fieldErrors.amount ? 'border-red-500 bg-red-50' : 'border-gray-200'
              }`}
              data-testid="bill-amount-input"
              aria-label="Bill amount"
              aria-invalid={fieldErrors.amount}
              aria-describedby={fieldErrors.amount ? "amount-error" : undefined}
            />
            {fieldErrors.amount && (
              <p id="amount-error" className="text-red-500 text-xs mt-1">Valid amount required</p>
            )}
          </div>
          <div>
            <select
              value={newBill.category}
              onChange={(e) => {
                setNewBill({ ...newBill, category: e.target.value });
                if (fieldErrors.category && e.target.value) {
                  setFieldErrors({ ...fieldErrors, category: false });
                }
              }}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white transition-colors ${
                fieldErrors.category ? 'border-red-500 bg-red-50' : 'border-gray-200'
              }`}
              data-testid="bill-category-select"
              aria-label="Bill category"
              aria-invalid={fieldErrors.category}
              aria-describedby={fieldErrors.category ? "category-error" : undefined}
            >
              <option value="">Category *</option>
              {BILL_CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            {fieldErrors.category && (
              <p id="category-error" className="text-red-500 text-xs mt-1">Category required</p>
            )}
          </div>
          <div>
            <input
              type="number"
              placeholder="Due day (1-31) *"
              min="1"
              max="31"
              value={newBill.due_day}
              onChange={(e) => {
                setNewBill({ ...newBill, due_day: e.target.value });
                const val = parseInt(e.target.value);
                if (fieldErrors.due_day && val >= 1 && val <= 31) {
                  setFieldErrors({ ...fieldErrors, due_day: false });
                }
              }}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors ${
                fieldErrors.due_day ? 'border-red-500 bg-red-50' : 'border-gray-200'
              }`}
              data-testid="bill-due-day-input"
              aria-label="Bill due day"
              aria-invalid={fieldErrors.due_day}
              aria-describedby={fieldErrors.due_day ? "due-day-error" : undefined}
            />
            {fieldErrors.due_day && (
              <p id="due-day-error" className="text-red-500 text-xs mt-1">Due day (1-31) required</p>
            )}
          </div>
          <select
            value={newBill.frequency}
            onChange={(e) => setNewBill({ ...newBill, frequency: e.target.value })}
            className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
            data-testid="bill-frequency-select"
            aria-label="Bill frequency"
          >
            <option value="Monthly">Monthly</option>
            <option value="Weekly">Weekly</option>
            <option value="Bi-Weekly">Bi-Weekly</option>
            <option value="Yearly">Yearly</option>
          </select>
        </div>
        <button
          onClick={handleAddBill}
          disabled={loading}
          className="w-full py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors"
          data-testid="add-bill-btn"
        >
          + Add Bill
        </button>
      </div>

      {/* Bills List */}
      {bills.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-bold text-gray-700 mb-3">
            Your Bills ({bills.length})
          </h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {bills.map((bill, index) => (
              <motion.div
                key={bill.id || index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
              >
                <div>
                  <div className="font-medium text-gray-900">{bill.name}</div>
                  <div className="text-xs text-gray-500">{bill.category}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-gray-900">
                    ${parseFloat(bill.amount).toFixed(2)}
                  </span>
                  <button
                    onClick={() => onRemoveBill(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    ✕
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-3 p-3 bg-emerald-50 rounded-xl">
            <div className="flex justify-between">
              <span className="text-emerald-700">Monthly Total</span>
              <span className="font-bold text-emerald-700">
                ${bills.reduce((sum, b) => sum + parseFloat(b.amount), 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        {/* Unlock Dashboard - Only shows after 3 bills */}
        {canUnlock && (
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={onUnlockDashboard}
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold text-lg rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 disabled:opacity-50 transition-all"
            data-testid="unlock-dashboard-btn"
          >
            {loading ? 'Saving...' : 'Unlock Dashboard 🚀'}
          </motion.button>
        )}

        {/* Save & Explore - Always visible */}
        <button
          onClick={onSaveAndExplore}
          disabled={loading}
          className="w-full py-3 border-2 border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-colors"
          data-testid="save-explore-btn"
        >
          {bills.length === 0 ? 'Skip & Explore App' : 'Save Progress & Explore App'}
        </button>
      </div>
    </motion.div>
  );
}

// ==================== MAIN COMPONENT ====================
export default function BillsOnboarding({ onComplete, onSkip, existingBills = [] }) {
  const [mode, setMode] = useState('choice'); // 'choice' | 'plaid' | 'plaid-review' | 'manual'
  const [linkToken, setLinkToken] = useState(null);
  const [plaidBills, setPlaidBills] = useState([]);
  const [manualBills, setManualBills] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch Plaid link token on mount
  useEffect(() => {
    const fetchLinkToken = async () => {
      try {
        const response = await apiPost('/plaid/create-link-token');
        if (response.ok) {
          const data = await response.json();
          setLinkToken(data.link_token);
        }
      } catch (error) {
        console.log('Plaid not available:', error);
      }
    };
    fetchLinkToken();
  }, []);

  // Plaid Link success handler
  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    setLoading(true);
    try {
      // Exchange token
      const exchangeResponse = await apiPost('/plaid/exchange-token', {
        public_token: publicToken
      });

      if (!exchangeResponse.ok) {
        throw new Error('Token exchange failed');
      }

      // Fetch recurring transactions
      const transactionsResponse = await apiGet('/plaid/recurring-transactions');
      if (transactionsResponse.ok) {
        const data = await transactionsResponse.json();
        setPlaidBills(data.bills || []);
        setMode('plaid-review');
        toast.success(`Found ${data.bills?.length || 0} recurring bills!`);
      } else {
        toast.error('Could not fetch bills. Try manual entry.');
        setMode('manual');
      }
    } catch (error) {
      console.error('Plaid error:', error);
      toast.error('Bank connection failed. Try manual entry.');
      setMode('manual');
    } finally {
      setLoading(false);
    }
  }, []);

  // Plaid Link config
  const { open: openPlaid, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: (err, metadata) => {
      if (err) {
        console.error('Plaid exit error:', err);
      }
      // User closed Plaid, stay on current screen
    }
  });

  // Handle mode selection
  const handleSelectPlaid = () => {
    if (plaidReady) {
      openPlaid();
    }
  };

  const handleSelectManual = () => {
    setMode('manual');
  };

  // Add bill manually
  const handleAddManualBill = (bill) => {
    setManualBills(prev => [...prev, { ...bill, id: `manual-${Date.now()}` }]);
  };

  // Remove manual bill
  const handleRemoveManualBill = (index) => {
    setManualBills(prev => prev.filter((_, i) => i !== index));
  };

  // Confirm Plaid bills
  const handleConfirmPlaidBills = async (bills) => {
    setLoading(true);
    try {
      // Add each bill to the backend with alias data
      for (const bill of bills) {
        await apiPost('/bills', {
          name: bill.name,
          amount: bill.amount,
          category: bill.category,
          frequency: bill.frequency,
          source: 'plaid',
          // Alias Catcher fields - store both custom alias and original Plaid name
          alias: bill.alias || null,
          original_plaid_name: bill.original_plaid_name || null,
          is_ecosystem_charge: bill.is_ecosystem_charge || false,
          platform: bill.platform || null
        });
      }

      // Mark as fully onboarded
      await updateOnboardingStatus('complete', bills.length);
      
      toast.success(`Added ${bills.length} bills to your budget!`);
      triggerConfetti();
      onComplete({ bills, source: 'plaid', isPartial: false });
    } catch (error) {
      console.error('Error adding bills:', error);
      toast.error('Error saving bills');
    } finally {
      setLoading(false);
    }
  };

  // Unlock Dashboard (Rule of 3 achieved)
  const handleUnlockDashboard = async () => {
    setLoading(true);
    try {
      // Add bills to backend
      for (const bill of manualBills) {
        await apiPost('/bills', {
          name: bill.name,
          amount: bill.amount,
          category: bill.category,
          due_day: bill.due_day,
          frequency: bill.frequency,
          source: 'manual'
        });
      }

      const isPartial = manualBills.length < 5;
      await updateOnboardingStatus(isPartial ? 'partial' : 'complete', manualBills.length);

      toast.success('Bills saved! Welcome to your dashboard!');
      if (manualBills.length >= 5) {
        triggerConfetti();
      }
      onComplete({ 
        bills: manualBills, 
        source: 'manual', 
        isPartial 
      });
    } catch (error) {
      console.error('Error saving bills:', error);
      toast.error('Error saving bills');
    } finally {
      setLoading(false);
    }
  };

  // Save & Explore (even with 0-2 bills)
  const handleSaveAndExplore = async () => {
    setLoading(true);
    try {
      // Add any bills they entered
      for (const bill of manualBills) {
        await apiPost('/bills', {
          name: bill.name,
          amount: bill.amount,
          category: bill.category,
          due_day: bill.due_day,
          frequency: bill.frequency,
          source: 'manual'
        });
      }

      await updateOnboardingStatus('partial', manualBills.length);

      onComplete({ 
        bills: manualBills, 
        source: 'manual', 
        isPartial: true,
        needsNudge: manualBills.length < 5
      });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update onboarding status in backend
  const updateOnboardingStatus = async (status, billCount) => {
    try {
      await apiPost('/auth/update-onboarding-status', {
        bills_onboarding_status: status,
        bills_count: billCount,
        needs_guapo_nudge: status === 'partial' && billCount < 5
      });
    } catch (error) {
      console.error('Error updating onboarding status:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8">
      <AnimatePresence mode="wait">
        {mode === 'choice' && (
          <ChoiceScreen
            key="choice"
            onSelectPlaid={handleSelectPlaid}
            onSelectManual={handleSelectManual}
            plaidReady={plaidReady && !!linkToken}
          />
        )}

        {mode === 'plaid-review' && (
          <PlaidBillsReview
            key="plaid-review"
            bills={plaidBills}
            onConfirm={handleConfirmPlaidBills}
            onBack={() => setMode('choice')}
            loading={loading}
          />
        )}

        {mode === 'manual' && (
          <GamifiedManualEntry
            key="manual"
            bills={manualBills}
            onAddBill={handleAddManualBill}
            onRemoveBill={handleRemoveManualBill}
            onSwitchToPlaid={handleSelectPlaid}
            onUnlockDashboard={handleUnlockDashboard}
            onSaveAndExplore={handleSaveAndExplore}
            loading={loading}
            plaidReady={plaidReady && !!linkToken}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Export sub-components for use elsewhere
export { ChoiceScreen, PlaidBillsReview, GamifiedManualEntry };
